import { useContext, useEffect } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { useSharedBotNameInput } from "@shared/features/bots/BotForm/common/hooks";
import useAmountSelector from "@shared/features/bots/BotForm/useAmountSelector";
import useBotFormSummary from "@shared/features/bots/BotForm/useBotFormSummary";
import { BOT_AMOUNT_PER_TRADE_TOOLTIP } from "@shared/features/onboarding/constants";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { useTheme } from "styled-components";
import { LanguageNameSpaces, OpenMessage } from "@shared/types";
import { isSymbol } from "@shared/types/utils";
import useSharedSymbolAndAccountSelectors from "@shared/features/bots/BotForm/useSharedSymbolAndAccountSelectors";
import { GroupOverviewContext } from "@shared/features/onboarding/GroupOverview.context";
import useAdvanced from "@shared/features/bots/BotForm/useAdvanced";
import useMarginSelectors from "@shared/features/bots/BotForm/useMarginSelectors";
import { useClientsData } from "@shared/utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useBotOverview = (openMessage: OpenMessage, isOnboarding: boolean) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const appTheme = useTheme();

  const { selectedAccountId, setSelectedAccountId } =
    useContext(GroupOverviewContext)!;
  const {
    symbols,
    setAccount,
    account,
    botId,
    publicBotGeneralData,
    isLoading,
  } = useContext(BotFormContext)!;

  const { getAccountExchangeById } = useClientsData();

  const accountExchange = selectedAccountId
    ? getAccountExchangeById(selectedAccountId)
    : undefined;

  const { botName } = useSharedBotNameInput({
    currentBotId: botId,
    initialize: true,
  });
  const { clientsData, getAccountById } = useClientsData();
  const {
    amountValueData,
    minAmountErrorMessage,
    valuesChanged: amountValuesChanged,
  } = useAmountSelector({
    isCopyPublicBotSummary: true,
    isOnboarding,
  });
  const defaultAccount = selectedAccountId
    ? getAccountById(selectedAccountId)
    : undefined;
  const { symbolIsValid } = useSharedSymbolAndAccountSelectors({
    defaultExchange: defaultAccount?.exchange,
    defaultSymbols: publicBotGeneralData?.symbols,
    defaultBlacklistSymbols: publicBotGeneralData?.blacklist_symbols,
    initialize: !!publicBotGeneralData && !!defaultAccount,
    openMessage,
  });
  const { maxBotAmountWarningMessage } = useBotFormSummary();
  useMarginSelectors({ openMessage });
  useAdvanced({ openMessage });
  const botSymbol = isSymbol(symbols) ? undefined : symbols?.[0];

  useEffect(() => {
    if (selectedAccountId) {
      setSelectedAccountId(undefined);
    }
  }, [botId]);

  useEffect(() => {
    if (account && selectedAccountId && clientsData) {
      const account = getAccountById(selectedAccountId);
      setAccount(account);
    }
  }, [account, selectedAccountId, clientsData]);

  const amountPerTradeHelperTooltip = {
    analyticsFieldName: "Amount per Trade",
    analyticsPageName: OnboardingDetailedStep.DCA_BOT_OVERVIEW,
    description: t(BOT_AMOUNT_PER_TRADE_TOOLTIP),
    iconStyle: { color: appTheme?.colors.secondaryGrey },
  };

  const saveButtonDisabled =
    !account || !symbolIsValid || !!minAmountErrorMessage;

  return {
    publicBotGeneralData,
    botId,
    botName,
    botSymbol,
    amountValueData,
    minAmountErrorMessage,
    maxBotAmountWarningMessage,
    amountPerTradeHelperTooltip,
    isLoading,
    saveButtonDisabled,
    accountExchange,
    amountValuesChanged,
  };
};

export default useBotOverview;
